import React from 'react';
import { Handle, Position } from 'reactflow';
import { PORT_CONFIG } from './port_config';

const POSITION_MAP = {
    top: Position.Top,
    bottom: Position.Bottom,
    left: Position.Left,
    right: Position.Right,
};

const SOURCE_STYLE = {
    width: 11,
    height: 11,
    background: '#22C55E',
    border: '2px solid #16A34A',
    borderRadius: '50%',
    zIndex: 10,
};

const TARGET_STYLE = {
    width: 11,
    height: 11,
    background: '#3B82F6',
    border: '2px solid #1D4ED8',
    borderRadius: '50%',
    zIndex: 10,
};

/** Small "E" / "S" badge rendered next to the handle inside the node boundary. */
function HandleLabel({ pos, offset, type }) {
    const label = type === 'source' ? 'S' : 'E';
    const bg    = type === 'source' ? '#14532D' : '#1E3A5F';
    const color = type === 'source' ? '#86EFAC' : '#93C5FD';

    // Position the label just inside the node edge, aligned with the handle center.
    const style = {
        position: 'absolute',
        fontSize: 7,
        fontWeight: 700,
        lineHeight: '10px',
        padding: '0 2px',
        borderRadius: 2,
        background: bg,
        color,
        pointerEvents: 'none',
        zIndex: 11,
        userSelect: 'none',
    };

    const pct = offset != null ? `${offset}%` : '50%';

    if (pos === 'left') {
        style.left = 3;
        style.top = pct;
        style.transform = 'translateY(-50%)';
    } else if (pos === 'right') {
        style.right = 3;
        style.top = pct;
        style.transform = 'translateY(-50%)';
    } else if (pos === 'top') {
        style.top = 3;
        style.left = pct;
        style.transform = 'translateX(-50%)';
    } else {
        style.bottom = 3;
        style.left = pct;
        style.transform = 'translateX(-50%)';
    }

    return <div style={style}>{label}</div>;
}

/**
 * HOC wrapper for all SCADA nodes.
 * Renders children FIRST (SVG stays below in DOM stack), then handles on top.
 * Reads PORT_CONFIG[element_type] to show only the correct ports per element.
 */
export default function BaseScadaNode({ data, selected, children, style = {} }) {
    const portDef = PORT_CONFIG[data?.element_type];
    const computed = typeof portDef === 'function' ? portDef(data) : (portDef ?? []);
    const ports = Array.isArray(computed) ? computed : [];

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                border: selected ? '2px solid #4F46E5' : '2px solid transparent',
                borderRadius: 4,
                boxSizing: 'border-box',
                transform: data?.rotation ? `rotate(${data.rotation}deg)` : undefined,
                transformOrigin: 'center',
                ...style,
            }}
        >
            {/* Floating label above node — shown when config.show_label is enabled */}
            {data?.config?.show_label && data?.label && (
                <div style={{
                    position: 'absolute',
                    top: -22,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(15,23,42,0.85)',
                    border: '1px solid #334155',
                    borderRadius: 4,
                    padding: '2px 7px',
                    fontSize: 10,
                    fontWeight: 600,
                    color: '#E2E8F0',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    zIndex: 20,
                    lineHeight: '16px',
                    backdropFilter: 'blur(4px)',
                }}>
                    {data.label}
                </div>
            )}

            {/* SVG content first — stays below handles in stacking order */}
            {children}

            {/* Error contactor overlay */}
            {(() => {
                const cfg = data?.config || {};
                const lv  = data?.liveValues || {};
                const thr = parseFloat(cfg.threshold ?? 0.5);
                const hasError = lv.error != null && parseFloat(lv.error) >= thr;
                return hasError ? (
                    <div style={{ position: 'absolute', top: 2, right: 2, background: '#7F1D1D',
                        border: '1px solid #EF4444', borderRadius: 3, padding: '1px 4px',
                        fontSize: 9, color: '#FEF2F2', fontWeight: 700,
                        pointerEvents: 'none', zIndex: 6, lineHeight: '14px' }}>
                        ⚠
                    </div>
                ) : null;
            })()}

            {/* Actuator lock overlay (pending command sent, waiting for feedback) */}
            {data?.isLocked && (
                <div style={{
                    position: 'absolute', inset: 0, borderRadius: 4, boxSizing: 'border-box',
                    background: 'rgba(245, 158, 11, 0.12)', border: '2px solid #F59E0B',
                    pointerEvents: 'none', zIndex: 5,
                }} />
            )}

            {/* Handles rendered AFTER children so they sit on top of the SVG */}
            {ports.map(p => {
                const base = p.type === 'source' ? SOURCE_STYLE : TARGET_STYLE;
                const extraStyle = {};
                if (p.offset != null) {
                    if (p.pos === 'top' || p.pos === 'bottom') {
                        extraStyle.left = `${p.offset}%`;
                        extraStyle.transform = 'translateX(-50%)';
                    } else if (p.pos === 'right') {
                        extraStyle.top = `${p.offset}%`;
                        extraStyle.transform = 'translate(50%, -50%)';
                    } else {
                        extraStyle.top = `${p.offset}%`;
                        extraStyle.transform = 'translate(-50%, -50%)';
                    }
                }
                // For the view-mode dot we must replicate ReactFlow's border placement:
                // RF puts the handle center exactly on the node edge, then our extraStyle
                // adjusts along the axis. We add the perpendicular edge anchor here.
                const pct = p.offset != null ? `${p.offset}%` : '50%';
                const viewDotStyle = { position: 'absolute', ...base, pointerEvents: 'none' };
                if (p.pos === 'left') {
                    viewDotStyle.left = 0;
                    viewDotStyle.top = pct;
                    viewDotStyle.transform = 'translate(-50%, -50%)';
                } else if (p.pos === 'right') {
                    viewDotStyle.right = 0;
                    viewDotStyle.top = pct;
                    viewDotStyle.transform = 'translate(50%, -50%)';
                } else if (p.pos === 'top') {
                    viewDotStyle.top = 0;
                    viewDotStyle.left = pct;
                    viewDotStyle.transform = 'translate(-50%, -50%)';
                } else {
                    viewDotStyle.bottom = 0;
                    viewDotStyle.left = pct;
                    viewDotStyle.transform = 'translate(-50%, 50%)';
                }

                return (
                    <React.Fragment key={p.id}>
                        {data?.isEditMode
                            ? <Handle
                                id={p.id}
                                type={p.type}
                                position={POSITION_MAP[p.pos]}
                                style={{ ...base, ...extraStyle }}
                                title={p.id}
                              />
                            : <>
                                {/* Invisible Handle so ReactFlow registers edge connection points in view mode */}
                                <Handle
                                    id={p.id}
                                    type={p.type}
                                    position={POSITION_MAP[p.pos]}
                                    style={{ opacity: 0, pointerEvents: 'none', ...extraStyle }}
                                    isConnectable={false}
                                />
                                <div style={viewDotStyle} title={p.id} />
                              </>
                        }
                        <HandleLabel pos={p.pos} offset={p.offset} type={p.type} />
                    </React.Fragment>
                );
            })}
        </div>
    );
}
